<template>
	<v-dialog
		:value="open"
		@input="$emit('update:open', $event)"
		fullscreen
		persistent
		transition="dialog-bottom-transition"
		style="overflow-x: hidden"
		scrollable>
		<v-card class="background-1 rounded-0" v-if="open">
			<v-card-title class="pa-3">
				<p class="pl-2 pl-xl-3">Importa come fatture di acquisto</p>
				<v-spacer></v-spacer>
				<v-btn icon @click.native.stop="close()"><v-icon>mdi-close</v-icon></v-btn>
			</v-card-title>
			<v-divider></v-divider>

			<v-card-text style="overflow-x: hidden" class="pa-0">
				<v-container class="px-6" fluid>
					<v-row class="mx-0">
						<v-col cols="12" class="display-flex px-1 pt-6 pb-0">
							<strong>Opzioni</strong>
						</v-col>
					</v-row>
					<v-card class="round-card background-2 pa-4 px-0 mt-6 pt-12 import-options">
						<fields-generator
							v-if="loaded"
							:fieldsset.sync="infoFieldsset"
							:model.sync="filters"
							ref="fieldsgenerator"
						></fields-generator>
						<v-alert v-if="missingSectionals.length" type="warning" text dense class="mx-6 mb-0 missing-sectionals">
							Seleziona {{ missingSectionals.join(' e ') }}: senza sezionale i documenti di quel tipo non si importano.
						</v-alert>
					</v-card>
				</v-container>

				<!-- Da risolvere: codici IVA mancanti, senza lasciare il dialog -->
				<v-container class="px-6" fluid v-if="unresolvedVats.length">
					<v-row class="mx-0">
						<v-col cols="12" class="display-flex px-1 pt-6 pb-0">
							<strong>Da risolvere</strong>
							<span class="grey--text">&nbsp;({{ unresolvedVats.length }})</span>
						</v-col>
					</v-row>
					<v-card class="round-card background-2 pa-4 mt-4 unresolved-vats">
						<div
							v-for="(pair, index) in unresolvedVats"
							:key="pair.label"
							:class="['d-flex flex-wrap align-center unresolved-vat', { 'pt-4 mt-4 border-top': index > 0 }]"
							style="gap: 12px"
						>
							<div class="unresolved-vat-text">
								<div><strong>IVA {{ pair.label }}</strong> senza codice IVA</div>
								<small class="d-block grey--text" v-if="pair.natura_label">{{ pair.natura_label }}</small>
								<small class="d-block grey--text">
									{{ pair.documents.length === 1 ? '1 documento' : pair.documents.length + ' documenti' }}:
									{{ pair.documents.map(d => d.number).join(', ') }}
								</small>
							</div>
							<v-spacer></v-spacer>
							<template v-if="canManageVat">
								<v-btn color="primary" small depressed @click="openVatDialog(pair)">
									<v-icon left small>mdi-plus</v-icon>
									Crea codice IVA
								</v-btn>
								<span class="grey--text body-2">oppure</span>
								<v-autocomplete
									v-model="mappingChoice[pair.label]"
									:items="vatCodeItems(pair)"
									label="Usa un codice esistente"
									dense
									outlined
									hide-details
									class="mapping-select"
								></v-autocomplete>
								<v-btn
									small
									outlined
									color="primary"
									:disabled="!mappingChoice[pair.label]"
									:loading="savingMapping === pair.label"
									@click="saveMapping(pair)"
								>Usa</v-btn>
							</template>
							<small v-else class="grey--text">
								Per creare il codice IVA o la mappatura serve il permesso Impostazioni del ruolo.
							</small>
						</div>
					</v-card>
				</v-container>

				<v-container class="px-6" fluid>
					<v-alert v-if="afterImportMessage" type="info" text dense class="mt-6 mb-0">{{ afterImportMessage }}</v-alert>
					<v-row class="mx-0">
						<v-col cols="12" class="display-flex px-1 pt-6 pb-0">
							<strong>Documenti da importare</strong>
							<span class="grey--text pr-4">&nbsp;({{ importable }})</span>
							<div class="display-flex" v-if="skipped > 0">
								<strong>Non importabili&nbsp;</strong>
								<span class="grey--text pr-4">&nbsp;({{ skipped }})</span>
							</div>
							<v-spacer></v-spacer>
							<v-switch
								v-if="skipped > 0"
								v-model="onlyBlocked"
								label="Solo non importabili"
								dense
								hide-details
								class="mt-0 pt-0"
							></v-switch>
						</v-col>
					</v-row>
				</v-container>

				<v-container class="px-6" fluid>
					<v-card class="round-card background-2 pa-4">
						<!-- l'analisi di molti documenti richiede qualche secondo -->
						<v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2 import-loading"></v-progress-linear>
						<v-simple-table dense>
							<template v-slot:default>
								<thead>
									<tr>
										<th>Numero</th>
										<th>Data</th>
										<th>Fornitore</th>
										<th>Righe</th>
										<th>Scadenze</th>
										<th>Esito</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="row in displayedRows" :key="row.id">
										<td>{{ row.number }}</td>
										<td>{{ row.date | date }}</td>
										<td>
											{{ row.summary.supplier_name }}
											<v-chip x-small color="warning" outlined class="ml-2" v-if="row.summary.supplier === 'create'">da creare</v-chip>
											<v-chip x-small color="error" outlined class="ml-2" v-else-if="row.summary.supplier === 'missing'">non trovato</v-chip>
										</td>
										<td>
											{{ row.summary.lines }}
											<small class="d-block grey--text" v-if="row.summary.lines_default_product">{{ row.summary.lines_default_product }} a prodotto generico</small>
											<small class="d-block grey--text" v-if="row.summary.lines_create_product">{{ row.summary.lines_create_product }} nuovi prodotti</small>
										</td>
										<td>
											{{ row.summary.deadlines }}
											<v-chip x-small outlined class="ml-2">{{ paymentTermLabel(row.summary.payment_term) }}</v-chip>
											<!-- rate dall'XML che la condizione proposta non riproduce -->
											<small class="d-block grey--text" v-if="row.summary.deadlines > 0 && row.summary.payment_term !== 'inferred'">rate dall'XML, modificate a mano</small>
										</td>
										<td>
											<v-chip small color="success" v-if="row.ok">importabile</v-chip>
											<template v-else>
												<v-chip small color="error" class="mr-1 my-1" v-for="block in row.blocks" :key="block">{{ block }}</v-chip>
												<!-- casi da sistemare a mano (es. fornitore da scegliere) -->
												<v-btn
													x-small
													text
													color="primary"
													:href="'#/invoices/edocuments/' + row.id + '/import'"
													target="_blank"
												>
													<v-icon left x-small>mdi-open-in-new</v-icon>
													Apri nel wizard
												</v-btn>
											</template>
										</td>
									</tr>
									<tr v-if="rows.length === 0">
										<td colspan="6">
											<span v-if="loading">Analisi {{ ids.length === 1 ? 'del documento' : 'di ' + ids.length + ' documenti' }} in corso…</span>
											<span v-else>Nessun documento selezionato</span>
										</td>
									</tr>
								</tbody>
							</template>
						</v-simple-table>
					</v-card>
					<small class="pl-2">Sono importabili i documenti ricevuti non già collegati a una fattura di acquisto (salvo reimportazione forzata).</small>
				</v-container>
			</v-card-text>

			<v-divider></v-divider>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text color="info" @click.native="close()">Annulla</v-btn>
				<v-btn text color="primary" @click.native="confirmImport = true" :loading="sending" :disabled="loading || importable === 0 || missingSectionals.length > 0">Procedi</v-btn>
			</v-card-actions>
		</v-card>

		<vat-code-dialog
			v-model="vatDialog.open"
			:proposal="vatDialog.proposal"
			:label="vatDialog.label"
			:types="vatTypes"
			@created="getData()"
		></vat-code-dialog>

		<confirm-dialog
			:confirmModel="confirmImport"
			mainTitle="Confermi l'importazione dei documenti selezionati?"
			secondTitle="Verranno create le fatture di acquisto (o note di credito fornitore) per i soli documenti importabili."
			@update:confirmModel="confirmImport = false"
			:atConfirm="importEdocuments"
			:confirm="{color: 'primary', label: 'importa'}"
		></confirm-dialog>
	</v-dialog>
</template>

<script>
	import VatCodeDialog from '@/components/modules/invoices/edocuments/VatCodeDialog'

	export default {
		name: 'import-edocuments-dialog',
		components: { VatCodeDialog },
		props: {
			params: Object,
			selected: Array,
			selectedIds: Array,
			modelDef: String,
			open: Boolean,
			button: Object,
		},

		data() {
			return {
				rows: [],
				infoFieldsset: [],
				filters: {},
				sending: false,
				loaded: false,
				confirmImport: false,
				lastFetchedFilters: null,
				// documenti del dialog: dopo "Procedi" restano solo i non importabili
				ids: [],
				loading: false,
				unresolvedVats: [],
				canManageVat: false,
				vatCodes: [],
				vatTypes: [],
				mappingChoice: {},
				savingMapping: null,
				onlyBlocked: false,
				afterImportMessage: '',
				vatDialog: { open: false, proposal: null, label: '' },
			}
		},

		computed: {
			// sezionali obbligatori per i tipi di documento presenti ma lasciati vuoti
			missingSectionals() {
				const fields = (this.infoFieldsset && this.infoFieldsset.fields) || []
				return fields
					.filter(field => field.key && field.key.indexOf('sectional_id_') === 0)
					.filter(field => !this.filters[field.key])
					.filter(field => this.rows.some(row => 'sectional_id_' + row.target === field.key))
					.map(field => '“' + field.label + '”')
			},

			// esito delle righe con il sezionale verificato qui: il motivo sul
			// sezionale arrivato dall'analisi si ricalcola dal campo attuale
			evaluatedRows() {
				const fields = (this.infoFieldsset && this.infoFieldsset.fields) || []
				return this.rows.map(row => {
					const summary = row.summary || {}
					const blocks = row.blocks.filter(block => block !== summary.sectional_block)
					const field = fields.find(f => f.key === 'sectional_id_' + row.target)
					if(field && !this.filters[field.key]) {
						blocks.unshift(field.label + ' non selezionato')
					}
					return Object.assign({}, row, { blocks, ok: blocks.length === 0 })
				})
			},
			importable() {
				return this.evaluatedRows.filter(row => row.ok).length
			},
			skipped() {
				return this.evaluatedRows.filter(row => !row.ok).length
			},
			// non importabili in cima, nell'ordine ricevuto
			displayedRows() {
				const blocked = this.evaluatedRows.filter(row => !row.ok)
				return this.onlyBlocked ? blocked : blocked.concat(this.evaluatedRows.filter(row => row.ok))
			},
		},

		watch: {
			open: {
				handler(newValue) {
					if(newValue) {
						this.ids = (this.selectedIds || []).slice()
						this.getInfo()
					} else {
						this.reset()
					}
				}
			},
			// solo "Reimporta" cambia l'esito dell'analisi; sezionali e data si
			// verificano nel dialog senza ricaricare i documenti
			'filters.force_reimport': function() {
				this.refreshIfFiltersChanged()
			},
		},

		methods: {

			getData() {
				this.lastFetchedFilters = JSON.stringify(!!this.filters.force_reimport)
				this.loading = true
				this.axios.post('import-edocuments-data', {ids: this.ids, filters: this.filters}).then(response => {
					this.loading = false
					this.$set(this, 'rows', response.data.data)
					this.unresolvedVats = response.data.unresolved_vats || []
					this.canManageVat = !!response.data.can_manage_vat
					this.vatCodes = response.data.vat_codes || []
					this.vatTypes = response.data.vat_types || []
					if(this.skipped === 0) {
						this.onlyBlocked = false
					}
				}, () => {
					this.loading = false
				})
			},

			// codici con la stessa aliquota per primi
			vatCodeItems(pair) {
				const sameRate = code => code.rate !== null && Math.abs(code.rate - pair.rate) < 0.01
				const item = code => ({
					value: code.value,
					text: code.text + ' (' + [code.rate !== null ? code.rate + '%' : null, code.natura].filter(Boolean).join(' ') + ')',
				})
				return this.vatCodes.filter(sameRate).map(item).concat(this.vatCodes.filter(code => !sameRate(code)).map(item))
			},

			saveMapping(pair) {
				this.savingMapping = pair.label
				this.axios.post('import-edocuments-vat-mappings', {rate: pair.rate, natura: pair.natura, vat: this.mappingChoice[pair.label]}).then(response => {
					this.savingMapping = null
					this.$delete(this.mappingChoice, pair.label)
					this.$store.commit('setSnackbar', {timeout: 6000, text: response.data.message, value: true, color: 'success'})
					this.getData()
				}, () => {
					this.savingMapping = null
				})
			},

			openVatDialog(pair) {
				this.vatDialog = { open: true, proposal: Object.assign({}, pair.proposal), label: pair.label }
			},

			// La watch su force_reimport puo' scattare subito dopo getInfo() che
			// valorizza i default con $set: senza questa guardia si otterrebbe una
			// seconda POST import-edocuments-data identica alla prima.
			refreshIfFiltersChanged() {
				if(!this.loaded) {
					return
				}
				if(JSON.stringify(!!this.filters.force_reimport) === this.lastFetchedFilters) {
					return
				}
				this.getData()
			},

			getInfo() {
				this.loading = true
				this.axios.get('import-edocuments-info').then(response => {
					this.infoFieldsset = response.data.fields

					_.forEach(this.infoFieldsset.fields, (value) => {
						if(value.type === 'checkbox') {
							this.$set(this.filters, value.key, '')
						}
						if(value.default) {
							this.$set(this.filters, value.key, value.default)
						}
					})

					this.loaded = true
					this.getData()
				}, () => {
					this.loading = false
				})
			},

			paymentTermLabel(paymentTerm) {
				// stessa terminologia dello step di conferma del wizard
				if(paymentTerm === 'inferred') return 'riconosciuta dall\'XML'
				if(paymentTerm === 'supplier') return 'condizione del fornitore'
				if(paymentTerm === 'default') return 'condizione delle impostazioni'
				return 'nessuna condizione'
			},

			importEdocuments() {
				this.sending = true

				let dataObject = {
					filters: this.filters,
					ids: this.evaluatedRows.filter((row) => row.ok === true).map((row) => row.id)
				}

				const blockedIds = this.evaluatedRows.filter((row) => row.ok !== true).map((row) => row.id)

				this.axios.post('import-edocuments', dataObject).then(response => {
					this.sending = false
					this.confirmImport = false
					this.$eventBusUtility.$emit('refreshTable_' + this.modelDef)
					this.$store.commit('setSnackbar', {timeout: 6000, text: response.data.message, value: true, color: response.data.status})
					if(blockedIds.length === 0) {
						this.close()
						return
					}
					// i non importabili restano nel dialog: si sistemano e si rilancia
					this.ids = blockedIds
					this.onlyBlocked = false
					this.afterImportMessage = 'Import avviato per ' + dataObject.ids.length + ' documenti. '
						+ (blockedIds.length === 1 ? 'Resta 1 documento da sistemare' : 'Restano ' + blockedIds.length + ' documenti da sistemare')
						+ ': risolvi qui e premi di nuovo Procedi.'
					this.getData()
				}, () => {
					this.sending = false
				})
			},

			reset() {
				this.rows = []
				this.filters = {}
				this.loaded = false
				this.sending = false
				this.confirmImport = false
				this.lastFetchedFilters = null
				this.ids = []
				this.loading = false
				this.unresolvedVats = []
				this.canManageVat = false
				this.vatCodes = []
				this.vatTypes = []
				this.mappingChoice = {}
				this.savingMapping = null
				this.onlyBlocked = false
				this.afterImportMessage = ''
				this.vatDialog = { open: false, proposal: null, label: '' }
			},

			close() {
				this.reset()
				// anche update:open, cosi' basta :open.sync nel padre
				this.$emit('update:open', false)
				this.$emit('close')
			},
		},
	}
</script>

<style scoped>
	.unresolved-vat-text {
		min-width: 240px;
		flex: 1 1 280px;
	}
	.mapping-select {
		min-width: 240px;
		max-width: 320px;
	}
	/* "Reimporta": cliccabile solo il checkbox, non tutta la riga della card */
	.import-options >>> .v-input--checkbox .v-input__slot {
		pointer-events: none;
	}
	.import-options >>> .v-input--checkbox .v-input--selection-controls__input {
		pointer-events: auto;
	}
	.border-top {
		border-top: 1px solid rgba(128, 128, 128, 0.25);
	}
</style>
